"use client"

import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { useTranslations } from "next-intl"
import { ChevronRight, CheckCircle2, Loader2 } from "lucide-react"
import type { ReportItemType, ReportReason } from "@/types/report"
import { DockSheet } from "@/components/ui/dock-sheet"
import { DialogCard } from "@/components/ui/dialog-card"
import { useMobile } from "@/hooks/use-mobile"
import { SHEET_Z_NESTED } from "@/lib/sheet-styles"
import { useToast } from "@/hooks/use-toast"
import { resolveStorageUrl, type ImageRefInput } from "@/lib/storage/public-url"
import { SQUARE_SM, imagePresetUrl } from "@/lib/images/presets"

interface ReportReasonOption {
  id: ReportReason
  label: string
  description?: string
}

interface ReportDialogProps {
  open: boolean
  onOpenChangeAction: (open: boolean) => void
  itemType: ReportItemType
  itemId: string
  itemTitle?: string
  currentUserId?: string
  reportedUserId?: string
  reportedUsername?: string
  /**
   * The reported user's avatar AS STORED by whatever surface opened the
   * dialog (`profiles.avatar_url`, `listing.sellerAvatarUrl`,
   * `message.user.avatar_url`, …) — i.e. bucket-relative since phase C.
   * Resolved once inside, see `resolvedReportedAvatarUrl`.
   */
  reportedAvatarUrl?: ImageRefInput | null
  alreadyReported?: boolean
  onReported?: () => void
}

export function ReportDialog({
  open,
  onOpenChangeAction,
  itemType,
  itemId,
  itemTitle,
  currentUserId,
  reportedUserId,
  reportedUsername,
  reportedAvatarUrl,
  alreadyReported,
  onReported,
}: ReportDialogProps) {
  /**
   * Resolved ONCE, used by both the `<img>` previews below and the POST body.
   *
   * - Display: the two previews rendered `src={reportedAvatarUrl}` straight
   *   from the column. After phase C that is a bucket-relative path, which the
   *   browser requests against the SITE origin — a silent 404 / broken avatar
   *   on every report sheet. (Found 2026-07-27 by the `RawImageRef` brand,
   *   via the listing-detail seller chain; all ~15 call sites shared it.)
   * - Persistence: `reports.reported_avatar_url` is one of the four columns
   *   that stay ABSOLUTE **by design** — it is a historical snapshot of what
   *   the reporter saw, and the host is part of the recorded fact, so phase C
   *   never rewrites it. Sending the resolved form is therefore correct here
   *   (this is NOT the display != persistence case of handoff §9).
   *
   * `resolveStorageUrl` is idempotent, so callers that already hand over an
   * absolute URL are unaffected.
   *
   * @see lib/storage/url.ts (columns that stay absolute by design)
   * @see .tondocs/images/HANDOFF_RELATIVE_STORAGE_PATHS_2026_07_27.md §1.1, §9
   */
  const resolvedReportedAvatarUrl = resolveStorageUrl(reportedAvatarUrl) ?? null
  /**
   * DISPLAY only. Both renders below are `w-10 h-10` circles (40 CSS px), so
   * they take `SQUARE_SM` (128 device px) instead of the full upload.
   *
   * Deliberately a SECOND variable rather than a preset applied above: the
   * value above is persisted to `reports.reported_avatar_url` by the POST body
   * in `handleSubmit`, as a historical snapshot of what the reporter saw, and a
   * `?width=96&…` render URL is not that snapshot. This IS the
   * display-vs-persistence split the comment above says does not apply to the
   * stored value.
   */
  const reportedAvatarDisplayUrl = resolvedReportedAvatarUrl
    ? imagePresetUrl(resolvedReportedAvatarUrl, SQUARE_SM)
    : null
  const t = useTranslations("report")
  const REPORT_REASONS: ReportReasonOption[] = [
    { id: "fake", label: t("reasons.fake.label"), description: t("reasons.fake.description") },
    { id: "spam", label: t("reasons.spam.label"), description: t("reasons.spam.description") },
    { id: "inappropriate", label: t("reasons.inappropriate.label"), description: t("reasons.inappropriate.description") },
    { id: "scam", label: t("reasons.scam.label"), description: t("reasons.scam.description") },
    { id: "harassment", label: t("reasons.harassment.label"), description: t("reasons.harassment.description") },
    { id: "other", label: t("reasons.other.label"), description: t("reasons.other.description") },
  ]
  const [selectedReason, setSelectedReason] = useState<ReportReasonOption | null>(null)
  const [additionalInfo, setAdditionalInfo] = useState("")
  const [step, setStep] = useState<"select" | "details" | "success">("select")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [alreadyReportedFromServer, setAlreadyReportedFromServer] = useState(false)
  const [isCheckingAlreadyReported, setIsCheckingAlreadyReported] = useState(false)
  const { toast } = useToast()
  const detailsTextareaRef = useRef<HTMLTextAreaElement>(null)
  const isMobile = useMobile()

  const resolvedAlreadyReported = Boolean(alreadyReported || alreadyReportedFromServer)

  useEffect(() => {
    setAlreadyReportedFromServer(false)
    setIsCheckingAlreadyReported(false)
  }, [itemType, itemId, currentUserId])

  useEffect(() => {
    if (!open) return
    if (!currentUserId) return
    if (alreadyReported || alreadyReportedFromServer) return

    let cancelled = false
    setIsCheckingAlreadyReported(true)

    ;(async () => {
      try {
        const url = `/api/reports/exists?itemType=${encodeURIComponent(itemType)}&itemId=${encodeURIComponent(itemId)}`
        const response = await fetch(url, { method: "GET" })
        if (!response.ok) return
        const data = await response.json()
        if (cancelled) return
        if (data?.alreadyReported) {
          setAlreadyReportedFromServer(true)
          onReported?.()
        }
      } catch {
      } finally {
        if (!cancelled) setIsCheckingAlreadyReported(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [open, currentUserId, itemType, itemId, alreadyReported, alreadyReportedFromServer])

  useLayoutEffect(() => {
    if (open && !isMobile && step === "details") {
      detailsTextareaRef.current?.focus()
    }
  }, [open, isMobile, step])

  const handleClose = () => {
    onOpenChangeAction(false)
    setTimeout(() => {
      setSelectedReason(null)
      setAdditionalInfo("")
      setStep("select")
    }, 300)
  }

  const handleSelectReason = (reason: ReportReasonOption) => {
    setSelectedReason(reason)
    setStep("details")
  }

  const handleSubmit = async () => {
    if (!selectedReason) return
    if (!currentUserId) {
      toast({
        title: t("signInRequired"),
        description: t("signInMessage"),
        variant: "destructive",
      })
      return
    }
    setIsSubmitting(true)

    try {
      const response = await fetch("/api/reports", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          itemType,
          itemId,
          reason: selectedReason.id,
          description: additionalInfo.trim() || null,
          reportedUserId: reportedUserId || null,
          reportedUsername: reportedUsername || null,
          reportedAvatarUrl: resolvedReportedAvatarUrl,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        const message = data.error || "Failed to submit report"
        if (message === "You have already reported this item") {
          setIsSubmitting(false)
          setAlreadyReportedFromServer(true)
          setStep("success")
          onReported?.()
          return
        }
        throw new Error(message)
      }

      setIsSubmitting(false)
      setAlreadyReportedFromServer(true)
      setStep("success")
      onReported?.()
    } catch (error: any) {
      setIsSubmitting(false)
      toast({
        title: t("failedTitle"),
        description: String(error?.message || t("failedMessage")),
        variant: "destructive",
      })
    }
  }

  const reportSubmitFooter =
    step === "details" && !resolvedAlreadyReported && !isCheckingAlreadyReported ? (
      <button
        type="button"
        onClick={() => void handleSubmit()}
        disabled={isSubmitting}
        className="w-full h-12 rounded-full text-base font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? t("submitting") : t("submitReport")}
      </button>
    ) : step === "success" ? (
      <button
        type="button"
        onClick={handleClose}
        className="w-full h-12 rounded-full text-base font-semibold bg-muted text-foreground hover:bg-sheet-handle transition-colors"
      >
        {t("done")}
      </button>
    ) : undefined

  if (isMobile) {
    return (
      <DockSheet
        open={open}
        onOpenChange={onOpenChangeAction}
        title={t("title", { itemType })}
        zIndex={SHEET_Z_NESTED}
        footer={reportSubmitFooter}
      >
        <div data-report-dialog>
          {resolvedAlreadyReported ? (
            <div className="text-center py-8">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <h4 className="text-foreground font-semibold mb-2">{t("alreadyReported")}</h4>
              <p className="text-muted-foreground text-base">
                {t("alreadyReportedMessage", { itemType })}
              </p>
            </div>
          ) : isCheckingAlreadyReported ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : step === "select" ? (
            <div className="space-y-1">
              {itemTitle ? (
                <p className="text-muted-foreground text-base line-clamp-2 mb-4">{itemTitle}</p>
              ) : null}
              <p className="text-muted-foreground text-base mb-4">{t("whyReporting", { itemType })}</p>
              {REPORT_REASONS.map((reason) => (
                <button
                  key={reason.id}
                  type="button"
                  onClick={() => handleSelectReason(reason)}
                  disabled={isCheckingAlreadyReported}
                  className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-muted transition-colors group"
                >
                  <div className="text-left">
                    <p className="text-foreground font-medium text-base">{reason.label}</p>
                    {reason.description && <p className="text-muted-foreground text-base mt-0.5">{reason.description}</p>}
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground/70 group-hover:text-foreground transition-colors" />
                </button>
              ))}
            </div>
          ) : step === "details" ? (
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => setStep("select")}
                className="text-muted-foreground text-base hover:text-foreground transition-colors"
              >
                {t("back")}
              </button>

              <div className="p-3 rounded-xl bg-muted border border-sheet-handle">
                <p className="text-foreground font-medium text-base">{selectedReason?.label}</p>
                {selectedReason?.description && (
                  <p className="text-muted-foreground text-base mt-1">{selectedReason.description}</p>
                )}
              </div>

              {reportedUsername && (
                <div className="flex items-center gap-3 p-3 rounded-xl bg-muted">
                  <img
                    src={reportedAvatarDisplayUrl || "/placeholder.svg"}
                    alt={reportedUsername}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="text-muted-foreground text-base">{t("reportedUser")}</p>
                    <p className="text-foreground font-medium text-base">@{reportedUsername}</p>
                  </div>
                </div>
              )}

              <div>
                <label className="text-muted-foreground text-base block mb-2">{t("additionalDetails")}</label>
                <textarea
                  ref={detailsTextareaRef}
                  value={additionalInfo}
                  onChange={(e) => setAdditionalInfo(e.target.value)}
                  placeholder={t("additionalPlaceholder")}
                  className="w-full h-24 px-4 py-3 rounded-xl bg-muted border border-sheet-handle text-base text-foreground placeholder:text-muted-foreground/70 resize-none focus:outline-none focus:ring-2 focus:ring-foreground/20 transition-colors"
                />
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <h4 className="text-foreground font-semibold mb-2">{t("submitted")}</h4>
              <p className="text-muted-foreground text-base">{t("submittedMessage")}</p>
            </div>
          )}
        </div>
      </DockSheet>
    )
  }

  return (
    <DialogCard
      open={open}
      onOpenChange={(next) => (next ? onOpenChangeAction(true) : handleClose())}
      title={t("title", { itemType })}
      belowTitle={
        itemTitle ? (
          <p className="text-muted-foreground text-xs line-clamp-1 text-center">{itemTitle}</p>
        ) : undefined
      }
      panelClassName="max-w-md"
      /* On the PANEL, not on a body wrapper: `ChatDialog` keeps itself open
         while this card is in use by testing `closest("[data-report-dialog]")`
         on the outside-click target, and a click on this card's CHROME row
         (its close button, its title) has to be covered by that test too. */
      panelDataAttributes={{ "data-report-dialog": "" }}
    >
      <>
        {resolvedAlreadyReported ? (
            <div className="text-center py-8">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <h4 className="text-foreground font-semibold mb-2">{t("alreadyReported")}</h4>
              <p className="text-muted-foreground text-sm">
                {t("alreadyReportedMessage", { itemType })}
              </p>
            </div>
          ) : isCheckingAlreadyReported ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : step === "select" ? (
            <div className="space-y-1">
              <p className="text-muted-foreground text-sm mb-4">{t("whyReporting", { itemType })}</p>
              {REPORT_REASONS.map((reason) => (
                <button
                  key={reason.id}
                  onClick={() => handleSelectReason(reason)}
                  disabled={isCheckingAlreadyReported}
                  className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-foreground/5 transition-colors group"
                >
                  <div className="text-left">
                    <p className="text-foreground font-medium">{reason.label}</p>
                    {reason.description && <p className="text-muted-foreground text-xs mt-0.5">{reason.description}</p>}
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground/70 group-hover:text-foreground transition-colors" />
                </button>
              ))}
            </div>
          ) : step === "details" ? (
            <div className="space-y-4">
              <button
                onClick={() => setStep("select")}
                className="text-muted-foreground text-sm hover:text-foreground transition-colors"
              >
                {t("back")}
              </button>

              <div className="p-3 rounded-xl bg-foreground/5 border border-foreground/10">
                <p className="text-foreground font-medium">{selectedReason?.label}</p>
                {selectedReason?.description && (
                  <p className="text-muted-foreground text-xs mt-1">{selectedReason.description}</p>
                )}
              </div>

              {reportedUsername && (
                <div className="flex items-center gap-3 p-3 rounded-xl bg-foreground/5">
                  <img
                    src={reportedAvatarDisplayUrl || "/placeholder.svg"}
                    alt={reportedUsername}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="text-muted-foreground text-xs">{t("reportedUser")}</p>
                    <p className="text-foreground font-medium">@{reportedUsername}</p>
                  </div>
                </div>
              )}

              <div>
                <label className="text-muted-foreground text-sm block mb-2">{t("additionalDetails")}</label>
                <textarea
                  ref={detailsTextareaRef}
                  value={additionalInfo}
                  onChange={(e) => setAdditionalInfo(e.target.value)}
                  placeholder={t("additionalPlaceholder")}
                  className="w-full h-24 px-4 py-3 rounded-xl bg-foreground/5 border border-foreground/10 text-foreground placeholder:text-muted-foreground/70 resize-none focus:outline-none focus:ring-2 focus:ring-foreground/20 transition-colors"
                />
              </div>

              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="w-full py-3 rounded-full bg-red-500 hover:bg-red-600 text-white font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? t("submitting") : t("submitReport")}
              </button>
            </div>
          ) : (
            <div className="text-center py-8">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <h4 className="text-foreground font-semibold mb-2">{t("submitted")}</h4>
              <p className="text-muted-foreground text-sm mb-6">
                {t("submittedMessage")}
              </p>
              <button
                onClick={handleClose}
                className="px-6 py-2.5 rounded-full bg-foreground/10 hover:bg-foreground/20 text-foreground font-medium transition-colors"
              >
                {t("done")}
              </button>
            </div>
          )}
      </>
    </DialogCard>
  )
}


